'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import GXGLayout from '@/components/GXGLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface GXGEvent {
  id: string;
  name: string;
  description: string | null;
  event_date: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  capacity: number;
  status: string;
  tags: string | null;
  is_featured: boolean;
  is_broadcasting: boolean;
  twitch_url: string | null;
  youtube_url: string | null;
  stream_embed_url: string | null;
  checkin_count: number;
  match_count: number;
  viewer_count: number;
}

interface Station {
  id: string;
  station_name: string;
  system: string | null;
  current_game: string | null;
  current_round: string | null;
  status: string;
  player_1: string | null;
  player_2: string | null;
  notes: string | null;
}

interface ScheduleItem {
  id: string;
  title: string;
  start_time: string | null;
  end_time: string | null;
  status: string;
  sort_order: number;
}

interface CheckIn {
  id: string;
  player_name: string;
  checked_in_at: string;
  method: string;
}

interface LeaderboardEntry {
  id: string;
  player_name: string;
  points: number;
  rank: number | null;
}

interface Match {
  id: string;
  round: number;
  match_number: number;
  player_1: string | null;
  player_2: string | null;
  winner: string | null;
  scheduled_time: string | null;
  status: string;
}

const statusGradients: Record<string, string> = {
  open: 'from-green-900 to-teal-900',
  in_progress: 'from-purple-900 to-indigo-900',
  paused: 'from-yellow-900 to-orange-900',
  complete: 'from-gray-900 to-slate-900',
};

const stationStatusLabel: Record<string, { label: string; color: string }> = {
  open: { label: 'Open', color: 'text-gxg-green' },
  in_progress: { label: 'In Progress', color: 'text-gxg-purple2' },
  paused: { label: 'Paused', color: 'text-yellow-400' },
  complete: { label: 'Complete', color: 'text-white/40' },
};

const scheduleStatusColor: Record<string, string> = {
  upcoming: 'text-white/40',
  live: 'text-gxg-pink2',
  complete: 'text-gxg-green',
  cancelled: 'text-red-400',
};

function StationModal({ station, onClose, isStaff, onUpdate }: {
  station: Station;
  onClose: () => void;
  isStaff: boolean;
  onUpdate: (id: string, updates: Partial<Station>) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...station });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const supabase = createClient();
    const { error } = await supabase
      .from('event_stations')
      .update({
        current_game: form.current_game,
        current_round: form.current_round,
        status: form.status,
        player_1: form.player_1,
        player_2: form.player_2,
        notes: form.notes,
        updated_at: new Date().toISOString(),
      })
      .eq('id', station.id);
    setSaving(false);
    if (!error) {
      onUpdate(station.id, form);
      setEditing(false);
    }
  };

  const stStatus = stationStatusLabel[station.status] || { label: station.status, color: 'text-white/60' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="glass-card p-6 w-full max-w-md neon-border-purple">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-base text-white">{station.station_name}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white/70">✕</button>
        </div>

        {!editing ? (
          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-white/40 block mb-0.5">Console/System</span><span className="text-white/80">{station.system || '—'}</span></div>
              <div><span className="text-white/40 block mb-0.5">Status</span><span className={stStatus.color}>{stStatus.label}</span></div>
              <div><span className="text-white/40 block mb-0.5">Current Game</span><span className="text-white/80">{station.current_game || '—'}</span></div>
              <div><span className="text-white/40 block mb-0.5">Round</span><span className="text-white/80">{station.current_round || '—'}</span></div>
              {station.player_1 && <div><span className="text-white/40 block mb-0.5">Player 1</span><span className="text-gxg-purple2">{station.player_1}</span></div>}
              {station.player_2 && <div><span className="text-white/40 block mb-0.5">Player 2</span><span className="text-gxg-purple2">{station.player_2}</span></div>}
            </div>
            {station.notes && (
              <div><span className="text-white/40 block mb-0.5">Notes</span><span className="text-white/60">{station.notes}</span></div>
            )}
            <div className="flex gap-2 mt-4">
              {isStaff && (
                <button onClick={() => setEditing(true)} className="flex-1 py-2 rounded-lg text-xs border border-purple-500/30 text-gxg-purple2 hover:bg-purple-500/10">
                  Update Station
                </button>
              )}
              <button onClick={onClose} className="flex-1 py-2 rounded-lg text-xs border border-white/10 text-white/50 hover:border-white/20">
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-white/40 block mb-1">Current Game</label>
              <input className="gxg-input w-full" value={form.current_game || ''} onChange={e => setForm({ ...form, current_game: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/40 block mb-1">Round</label>
                <input className="gxg-input w-full" value={form.current_round || ''} onChange={e => setForm({ ...form, current_round: e.target.value })} />
              </div>
              <div>
                <label className="text-white/40 block mb-1">Status</label>
                <select className="gxg-select w-full" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {['open', 'in_progress', 'paused', 'complete'].map(s => <option key={s} value={s}>{stationStatusLabel[s]?.label || s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-white/40 block mb-1">Player 1</label>
                <input className="gxg-input w-full" value={form.player_1 || ''} onChange={e => setForm({ ...form, player_1: e.target.value })} />
              </div>
              <div>
                <label className="text-white/40 block mb-1">Player 2</label>
                <input className="gxg-input w-full" value={form.player_2 || ''} onChange={e => setForm({ ...form, player_2: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-white/40 block mb-1">Notes</label>
              <textarea className="gxg-input w-full resize-none h-14" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex gap-2 mt-2">
              <button onClick={handleSave} disabled={saving} className="flex-1 py-2 rounded-lg text-xs bg-purple-500/15 border border-purple-500/40 text-gxg-purple2 hover:bg-purple-500/25 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button onClick={() => setEditing(false)} className="flex-1 py-2 rounded-lg text-xs border border-white/10 text-white/50">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EventsPage() {
  const { profile, isStaffOrAbove } = useAuth();
  const [event, setEvent] = useState<GXGEvent | null>(null);
  const [stations, setStations] = useState<Station[]>([]);
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [broadcastSaving, setBroadcastSaving] = useState(false);
  const isStaff = isStaffOrAbove();

  const loadEventData = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = createClient();

      // Load featured/live event
      const { data: eventData } = await supabase
        .from('events')
        .select('*')
        .or('status.eq.live,is_featured.eq.true')
        .order('event_date', { ascending: false })
        .limit(1)
        .single();

      if (!eventData) {
        // Try any event
        const { data: anyEvent } = await supabase
          .from('events')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        setEvent(anyEvent || null);
        if (anyEvent) await loadRelatedData(anyEvent.id);
      } else {
        setEvent(eventData);
        await loadRelatedData(eventData.id);
      }
    } catch (err) {
      console.error('Load event error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRelatedData = async (eventId: string) => {
    const supabase = createClient();
    const [stationsRes, scheduleRes, checkInsRes, leaderboardRes, matchesRes] = await Promise.all([
      supabase.from('event_stations').select('*').eq('event_id', eventId).order('created_at'),
      supabase.from('event_schedule_items').select('*').eq('event_id', eventId).order('sort_order'),
      supabase.from('event_check_ins').select('id, player_name, checked_in_at, method').eq('event_id', eventId).order('checked_in_at', { ascending: false }).limit(20),
      supabase.from('event_leaderboard').select('*').eq('event_id', eventId).order('points', { ascending: false }).limit(10),
      supabase.from('tournament_matches').select('*').eq('event_id', eventId).order('round').order('match_number'),
    ]);

    if (!stationsRes.error) setStations(stationsRes.data || []);
    if (!scheduleRes.error) setSchedule(scheduleRes.data || []);
    if (!checkInsRes.error) setCheckIns(checkInsRes.data || []);
    if (!leaderboardRes.error) setLeaderboard(leaderboardRes.data || []);
    if (!matchesRes.error) setMatches(matchesRes.data || []);
  };

  useEffect(() => {
    loadEventData();
  }, [loadEventData]);

  const toggleBroadcast = async () => {
    if (!event || !isStaff) return;
    setBroadcastSaving(true);
    const supabase = createClient();
    const newVal = !event.is_broadcasting;
    const { error } = await supabase
      .from('events')
      .update({ is_broadcasting: newVal, updated_at: new Date().toISOString() })
      .eq('id', event.id);
    if (!error) setEvent(prev => prev ? { ...prev, is_broadcasting: newVal } : null);
    setBroadcastSaving(false);
  };

  const updateStation = (id: string, updates: Partial<Station>) => {
    setStations(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
    if (selectedStation?.id === id) setSelectedStation(prev => prev ? { ...prev, ...updates } : null);
  };

  const formatCheckinTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const upcomingMatches = matches.filter(m => m.status === 'scheduled' || m.status === 'live');
  const rounds = [...new Set(matches.map(m => m.round))].sort((a, b) => a - b);

  if (loading) {
    return (
      <GXGLayout>
        <div className="flex items-center justify-center h-64">
          <svg className="animate-spin w-8 h-8 text-gxg-purple2" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
          <span className="ml-3 text-white/50">Loading live event...</span>
        </div>
      </GXGLayout>
    );
  }

  if (!event) {
    return (
      <GXGLayout>
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-6">
            <h1 className="font-display font-black text-3xl md:text-4xl text-white mb-2">
              Live Event <span className="gxg-pink-text">Mode</span>
            </h1>
          </div>
          <div className="glass-card p-12 text-center">
            <div className="text-4xl mb-3">🎮</div>
            <h2 className="font-display font-bold text-xl text-white mb-2">No Live Events Right Now</h2>
            <p className="text-white/50 text-sm">Check back soon for upcoming GXG events and tournaments.</p>
            {isStaff && (
              <Link href="/admin/events" className="btn-gxg-primary px-6 py-2.5 rounded-lg text-sm inline-block mt-5">
                Create Event in Admin →
              </Link>
            )}
          </div>
        </div>
      </GXGLayout>
    );
  }

  const isLive = event.status === 'live';
  const broadcastState = event.is_broadcasting;

  return (
    <GXGLayout>
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-6">
          <h1 className="font-display font-black text-3xl md:text-4xl text-white mb-2">
            Live Event <span className="gxg-pink-text">Mode</span>
          </h1>
          <p className="text-white/50 text-sm">Real-time GXG event tracking.</p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-5">
            {/* Featured Event Card */}
            <div className="glass-card p-5 neon-border-pink relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-900/10 to-purple-900/10 pointer-events-none" />
              <div className="relative z-10">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full md:w-48 h-32 rounded-lg bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center flex-shrink-0 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <span className="font-display font-black text-2xl text-gxg-pink opacity-80 relative z-10">GXG</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {isLive ? (
                        <span className="badge-green text-[10px] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-gxg-green animate-pulse" />LIVE NOW</span>
                      ) : (
                        <span className="badge-blue text-[10px]">{event.status.toUpperCase()}</span>
                      )}
                    </div>
                    <h2 className="font-display font-bold text-xl text-white mb-1">{event.name}</h2>
                    {event.description && <p className="text-white/50 text-sm mb-2">{event.description}</p>}
                    {event.tags && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {event.tags.split(',').map(t => (
                          <span key={t} className="badge-purple">{t.trim()}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-4 text-xs text-white/40">
                      {event.location && <span>📍 {event.location}</span>}
                      {event.start_time && <span>🕐 {event.start_time}{event.end_time ? ` – ${event.end_time}` : ''}</span>}
                    </div>
                  </div>
                  <div className="flex-shrink-0 space-y-2">
                    <div className="glass-card p-3 text-xs space-y-1">
                      <div className="flex justify-between gap-4">
                        <span className="text-white/40">Live Status</span>
                        <span className={isLive ? 'text-gxg-green font-semibold' : 'text-white/50'}>{isLive ? 'ACTIVE' : event.status.toUpperCase()}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-white/40">Check-ins</span>
                        <span className="text-white/80">{checkIns.length} / {event.capacity}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-white/40">Matches</span>
                        <span className="text-white/80">{matches.length}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-white/40">Stations</span>
                        <span className="text-white/80">{stations.length}</span>
                      </div>
                    </div>
                    {isStaff && (
                      <>
                        <Link href={`/admin/events/${event.id}/manage`} className="btn-gxg-pink text-xs px-3 py-2 rounded-lg w-full block text-center">
                          Manage Event →
                        </Link>
                        <button
                          onClick={toggleBroadcast}
                          disabled={broadcastSaving}
                          className={`text-xs px-3 py-2 rounded-lg w-full flex items-center justify-center gap-1.5 transition-all disabled:opacity-50 ${
                            broadcastState
                              ? 'bg-red-500/20 text-red-400 border border-red-500/30' :'btn-gxg-outline'
                          }`}
                        >
                          {broadcastSaving ? '...' : broadcastState ? '🔴 Broadcasting Live' : '📡 Start Broadcasting'}
                        </button>
                      </>
                    )}
                    {!isStaff && broadcastState && (
                      <div className="text-xs px-3 py-2 rounded-lg w-full text-center bg-red-500/20 text-red-400 border border-red-500/30 flex items-center justify-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                        Broadcasting Live
                      </div>
                    )}
                    {broadcastState && (event.twitch_url || event.youtube_url || event.stream_embed_url) && (
                      <a
                        href={event.twitch_url || event.youtube_url || event.stream_embed_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-gxg-primary text-xs px-3 py-2 rounded-lg w-full block text-center"
                      >
                        📺 Watch Live →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Now Playing Stations */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-base text-white">Now Playing — <span className="text-gxg-purple2">Live Stations</span></h2>
                <span className="text-white/30 text-xs">{stations.length} station{stations.length !== 1 ? 's' : ''}</span>
              </div>
              {stations.length === 0 ? (
                <div className="text-center py-8 text-white/30 text-sm">
                  No stations configured yet.
                  {isStaff && <Link href={`/admin/events/${event.id}/manage`} className="block mt-2 text-gxg-purple2 text-xs">Add stations in Admin →</Link>}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {stations.map(station => {
                    const gradient = statusGradients[station.status] || 'from-gray-900 to-slate-900';
                    const stStatus = stationStatusLabel[station.status] || { label: station.status, color: 'text-white/60' };
                    return (
                      <button
                        key={station.id}
                        onClick={() => setSelectedStation(station)}
                        className="game-card text-left hover:scale-[1.02] transition-transform cursor-pointer"
                      >
                        <div className={`h-24 bg-gradient-to-br ${gradient} flex items-center justify-center relative`}>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center relative z-10">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                          </div>
                        </div>
                        <div className="p-2">
                          <div className="text-[9px] font-mono text-white/40 uppercase mb-0.5">{station.station_name}</div>
                          <div className="text-xs font-semibold text-white/90 leading-tight mb-1 truncate">{station.current_game || 'No game set'}</div>
                          <div className="flex items-center justify-between">
                            <span className="badge-purple text-[9px]">{station.system || '—'}</span>
                            <span className={`text-[10px] ${stStatus.color}`}>{stStatus.label}</span>
                          </div>
                          {station.current_round && (
                            <div className="text-[10px] text-white/35 mt-0.5">Round {station.current_round}</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bracket + Matches + Check-ins */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Tournament Bracket */}
              <div className="glass-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display font-bold text-xs text-white">Tournament Bracket</h3>
                </div>
                {matches.length === 0 ? (
                  <div className="text-center py-6 text-white/30 text-xs">
                    No bracket data yet.
                    {isStaff && <Link href={`/admin/events/${event.id}/manage`} className="block mt-1 text-gxg-purple2">Add matches →</Link>}
                  </div>
                ) : (
                  <div>
                    {rounds.slice(0, 2).map(round => (
                      <div key={round} className="mb-2">
                        <div className="text-[9px] text-white/30 font-mono uppercase mb-1">Round {round}</div>
                        {matches.filter(m => m.round === round).slice(0, 3).map(m => (
                          <div key={m.id} className={`bg-white/5 rounded px-2 py-1 text-[10px] mb-1 ${m.status === 'live' ? 'border border-gxg-pink2/30' : ''}`}>
                            <span className={m.winner === m.player_1 ? 'text-gxg-green font-semibold' : 'text-white/70'}>{m.player_1 || 'TBD'}</span>
                            <span className="text-white/30 mx-1">vs</span>
                            <span className={m.winner === m.player_2 ? 'text-gxg-green font-semibold' : 'text-white/70'}>{m.player_2 || 'TBD'}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                    {rounds.length > 2 && (
                      <div className="text-[10px] text-white/30 text-center mt-1">+{rounds.length - 2} more rounds</div>
                    )}
                  </div>
                )}
              </div>

              {/* Upcoming Matches */}
              <div className="glass-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display font-bold text-xs text-white">Upcoming Matches</h3>
                </div>
                {upcomingMatches.length === 0 ? (
                  <div className="text-center py-6 text-white/30 text-xs">No upcoming matches.</div>
                ) : (
                  <div className="space-y-2">
                    {upcomingMatches.slice(0, 4).map(m => (
                      <div key={m.id} className={`p-2 rounded-lg bg-white/3 ${m.status === 'live' ? 'border border-gxg-pink2/30' : ''}`}>
                        <div className="flex justify-between text-[10px] mb-0.5">
                          <span className="text-white/70 font-mono">{m.scheduled_time || '—'}</span>
                          {m.status === 'live' && <span className="text-gxg-pink2 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-gxg-pink2 animate-pulse" />LIVE</span>}
                        </div>
                        <div className="text-[11px] font-semibold text-white/80">
                          {m.player_1 || 'TBD'} vs {m.player_2 || 'TBD'}
                        </div>
                        <div className="text-[10px] text-white/35">Round {m.round} · Match {m.match_number}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Player Check-ins */}
              <div className="glass-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display font-bold text-xs text-white">Player Check-ins</h3>
                  <span className="text-[10px] text-gxg-green">{checkIns.length} checked in</span>
                </div>
                {checkIns.length === 0 ? (
                  <div className="text-center py-6 text-white/30 text-xs">
                    No check-ins yet.
                    <Link href={`/events/${event.id}/check-in`} className="block mt-1 text-gxg-purple2">Check in →</Link>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {checkIns.slice(0, 6).map(c => (
                      <div key={c.id} className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">
                          {c.player_name.slice(0, 2).toUpperCase()}
                        </div>
                        <span className="text-[11px] text-white/80 flex-1 truncate">{c.player_name}</span>
                        <span className="text-[10px] text-gxg-green">✓</span>
                        <span className="text-[10px] text-white/30">{formatCheckinTime(c.checked_in_at)}</span>
                      </div>
                    ))}
                    {checkIns.length > 6 && (
                      <div className="text-[10px] text-white/30 text-center">+{checkIns.length - 6} more</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Event Schedule */}
            <div className="glass-card p-5">
              <h2 className="font-display font-bold text-base text-white mb-4">Event Schedule</h2>
              {schedule.length === 0 ? (
                <div className="text-center py-6 text-white/30 text-sm">
                  No schedule items yet.
                  {isStaff && <Link href={`/admin/events/${event.id}/manage`} className="block mt-2 text-gxg-purple2 text-xs">Add schedule in Admin →</Link>}
                </div>
              ) : (
                <div className="space-y-2">
                  {schedule.map(item => (
                    <div
                      key={item.id}
                      className={`flex items-center gap-4 p-3 rounded-lg ${item.status === 'live' ? 'bg-pink-500/10 border border-gxg-pink2/30' : 'bg-white/3'}`}
                    >
                      <div className="text-xs font-mono text-white/50 w-20 flex-shrink-0">{item.start_time || '—'}</div>
                      <div className="flex-1">
                        <span className={`text-xs font-semibold ${item.status === 'live' ? 'text-white' : 'text-white/80'}`}>{item.title}</span>
                      </div>
                      <span className={`text-[10px] font-mono ${scheduleStatusColor[item.status] || 'text-white/40'}`}>
                        {item.status === 'live' ? '● LIVE' : item.status.toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-5">
            {/* Live Leaderboard */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-display font-bold text-base text-white">Live Leaderboard</h2>
                {isLive && <span className="flex items-center gap-1 text-[10px] text-gxg-green"><span className="w-1.5 h-1.5 rounded-full bg-gxg-green animate-pulse" />LIVE</span>}
              </div>
              <div className="flex justify-between text-[9px] text-white/30 font-mono uppercase mb-3 px-2">
                <span>#</span><span>Player</span><span>Points</span>
              </div>
              {leaderboard.length === 0 ? (
                <div className="text-center py-6 text-white/30 text-xs">No leaderboard data yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {leaderboard.map((entry, i) => (
                    <div
                      key={entry.id}
                      className={`flex items-center gap-3 px-2 py-2 rounded-lg ${i === 0 ? 'bg-yellow-500/10 border border-yellow-500/20' : i === 1 ? 'bg-white/5' : i === 2 ? 'bg-white/3' : ''}`}
                    >
                      <span className={`text-xs font-mono w-5 text-center ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-white/60' : i === 2 ? 'text-amber-600' : 'text-white/30'}`}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                      </span>
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">
                        {entry.player_name.slice(0, 2).toUpperCase()}
                      </div>
                      <span className="flex-1 text-xs text-white/80 truncate">{entry.player_name}</span>
                      <span className="text-xs font-mono text-gxg-gold">{entry.points.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fast Check-in QR */}
            <div className="glass-card p-5">
              <h2 className="font-display font-bold text-sm text-white mb-3">Fast Check-in</h2>
              <div className="bg-white rounded-lg p-3 flex items-center justify-center mb-3">
                <div className="text-center">
                  <div className="grid grid-cols-7 gap-0.5 mb-1">
                    {Array.from({ length: 49 }).map((_, i) => (
                      <div key={i} className={`w-3 h-3 ${Math.random() > 0.5 ? 'bg-black' : 'bg-white'}`} />
                    ))}
                  </div>
                  <p className="text-black text-[9px] font-mono mt-1">Scan to Check In</p>
                </div>
              </div>
              <p className="text-white/40 text-[10px] text-center mb-3">Scan QR or tap below to check in</p>
              <Link
                href={`/events/${event.id}/check-in`}
                className="btn-gxg-primary w-full py-2.5 rounded-lg text-xs text-center block"
              >
                Check In Now →
              </Link>
            </div>

            {/* Event Info */}
            <div className="glass-card p-5">
              <h2 className="font-display font-bold text-sm text-white mb-3">Event Info</h2>
              <div className="space-y-2 text-xs">
                {event.event_date && (
                  <div className="flex justify-between">
                    <span className="text-white/40">Date</span>
                    <span className="text-white/70">{new Date(event.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                )}
                {event.location && (
                  <div className="flex justify-between">
                    <span className="text-white/40">Location</span>
                    <span className="text-white/70">{event.location}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-white/40">Capacity</span>
                  <span className="text-white/70">{event.capacity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40">Broadcast</span>
                  <span className={broadcastState ? 'text-red-400' : 'text-white/40'}>
                    {broadcastState ? '🔴 Live' : 'Not Broadcasting'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Station Modal */}
      {selectedStation && (
        <StationModal
          station={selectedStation}
          onClose={() => setSelectedStation(null)}
          isStaff={isStaff}
          onUpdate={updateStation}
        />
      )}
    </GXGLayout>
  );
}
